import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  Download, ExternalLink, ChevronLeft, ChevronRight, FileText, Loader2,
  ThumbsUp, Target, ZoomIn, ZoomOut, Maximize2, ScanEye,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Iter194 — Inline preview drawer for attachments (images + PDFs).
 *
 * Renders as a right-side sheet. Images use a plain <img>, PDFs use an
 * <iframe> pointing at the same ACL-checked `/api/files/{path}` URL,
 * everything else falls back to a "Download to open" affordance.
 *
 * Props:
 *   open        — controlled boolean
 *   onOpenChange — controlled setter
 *   attachments — the FULL list of the parent's attachments (used for
 *                 next/prev navigation)
 *   initialIndex — which one to display first
 */
export function AttachmentPreviewDrawer({
  open, onOpenChange, attachments = [], initialIndex = 0,
  onReactionChange, onOcrRetried,
}) {
  const { user } = useAuth();
  const [idx, setIdx] = useState(initialIndex);
  const [imgLoaded, setImgLoaded] = useState(false);
  // Iter195 — zoom (images + PDFs) via CSS transform on the preview
  // surface. Scale is applied so long contracts don't need horizontal
  // scroll and reps can zoom into a screenshot number.
  const [zoom, setZoom] = useState(1);
  const [reactions, setReactions] = useState({}); // { file_id: {counts, mine} }
  // Iter196 — OCR retry state. Managers can force a re-run when the LLM
  // was down at upload time and nothing landed.
  const [ocrRetrying, setOcrRetrying] = useState(false);
  const isPrivileged = ["manager", "admin", "superadmin", "owner"].includes(
    (user?.role || "").toLowerCase()
  );
  const canReact = isPrivileged;

  useEffect(() => {
    setIdx(initialIndex);
    setImgLoaded(false);
    setZoom(1);
  }, [initialIndex, open]);

  // Iter195 — bulk-fetch reactions for the visible list when the drawer opens.
  useEffect(() => {
    if (!open || attachments.length === 0) return;
    let cancelled = false;
    const ids = attachments.map((a) => a.file_id).join(",");
    (async () => {
      try {
        const r = await fetch(`${API}/attachments/reactions/bulk?file_ids=${ids}`, {
          credentials: "include",
        });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setReactions(d.reactions || {});
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [open, attachments]);

  if (!attachments.length) return null;
  const safeIdx = Math.min(Math.max(idx, 0), attachments.length - 1);
  const current = attachments[safeIdx];
  if (!current) return null;

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const href = `${backendUrl}${current.url}`;
  const type = String(current.content_type || "");
  const isImage = type.startsWith("image/");
  const isPdf = type === "application/pdf";
  const currentReactions = reactions[current.file_id] || { counts: { thumbs_up: 0, target: 0 }, mine: [] };

  const step = (delta) => {
    setIdx((v) => {
      const next = (v + delta + attachments.length) % attachments.length;
      setImgLoaded(false);
      setZoom(1);
      return next;
    });
  };

  const toggleReaction = async (rt) => {
    if (!canReact) {
      toast.info("Only managers can react to attachments.");
      return;
    }
    try {
      const r = await fetch(`${API}/attachments/${current.file_id}/reactions`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reaction_type: rt }),
      });
      const body = await r.json();
      if (!r.ok) {
        toast.error(body?.detail || "Reaction failed.");
        return;
      }
      setReactions((prev) => ({
        ...prev,
        [current.file_id]: { counts: body.counts, mine: body.mine },
      }));
      if (onReactionChange) onReactionChange(current.file_id, body);
    } catch {
      toast.error("Network error.");
    }
  };

  // Iter196 — Manager-triggered OCR re-run. Fires against
  // `POST /api/attachments/{file_id}/ocr-retry`; the backend rejects
  // non-managers with 403 and non-images with 400.
  const retryOcr = async () => {
    if (!isPrivileged) {
      toast.info("Only managers can retry OCR.");
      return;
    }
    if (!isImage) {
      toast.info("OCR only runs on image attachments.");
      return;
    }
    setOcrRetrying(true);
    try {
      const r = await fetch(`${API}/attachments/${current.file_id}/ocr-retry`, {
        method: "POST", credentials: "include",
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(body?.detail || "OCR retry failed.");
        return;
      }
      const ex = body?.extracted_numbers || {};
      const bits = [];
      if (ex.cash_collected) bits.push(`$${ex.cash_collected} cash`);
      if (ex.close_count) bits.push(`${ex.close_count} closes`);
      if (ex.show_count) bits.push(`${ex.show_count} shows`);
      const summary = bits.length ? bits.join(" · ") : "no numbers found";
      toast.success(`OCR re-run: ${summary}`);
      if (onOcrRetried) onOcrRetried(current.file_id, body);
    } catch {
      toast.error("Network error.");
    } finally {
      setOcrRetrying(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="attachment-preview-drawer"
        className="w-full sm:max-w-2xl bg-[#0e0e0e] border-white/10 text-white overflow-hidden flex flex-col"
      >
        <SheetHeader className="pb-3 border-b border-white/10">
          <SheetTitle className="text-white flex items-center gap-2 pr-8 truncate">
            {current.original_filename || "Attachment"}
          </SheetTitle>
          <SheetDescription className="text-gray-400 flex items-center gap-3 text-xs">
            <span>{(Number(current.size) / 1024).toFixed(1)} KB</span>
            <span>·</span>
            <span>{type || "unknown"}</span>
            {attachments.length > 1 && (
              <>
                <span>·</span>
                <span data-testid="attachment-preview-index">
                  {safeIdx + 1} of {attachments.length}
                </span>
              </>
            )}
          </SheetDescription>
        </SheetHeader>

        {/* Zoom toolbar — only relevant for images and PDFs */}
        {(isImage || isPdf) && (
          <div
            className="flex items-center gap-2 px-1 py-1"
            data-testid="attachment-preview-zoom-toolbar"
          >
            <Button
              type="button" size="sm" variant="ghost"
              onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}
              disabled={zoom <= 0.5}
              data-testid="attachment-preview-zoom-out"
              className="h-7 px-2 text-gray-300 hover:text-white hover:bg-white/10"
              aria-label="Zoom out"
            >
              <ZoomOut className="w-3.5 h-3.5" />
            </Button>
            <span
              className="text-[11px] text-gray-400 min-w-[42px] text-center"
              data-testid="attachment-preview-zoom-level"
            >
              {Math.round(zoom * 100)}%
            </span>
            <Button
              type="button" size="sm" variant="ghost"
              onClick={() => setZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)))}
              disabled={zoom >= 3}
              data-testid="attachment-preview-zoom-in"
              className="h-7 px-2 text-gray-300 hover:text-white hover:bg-white/10"
              aria-label="Zoom in"
            >
              <ZoomIn className="w-3.5 h-3.5" />
            </Button>
            <div className="h-4 w-px bg-white/10 mx-1" />
            <Button
              type="button" size="sm" variant="ghost"
              onClick={() => setZoom(1)}
              data-testid="attachment-preview-fit-width"
              className="h-7 px-2 text-gray-300 hover:text-white hover:bg-white/10 text-[11px]"
            >
              <Maximize2 className="w-3.5 h-3.5 mr-1" />
              Fit
            </Button>
          </div>
        )}

        {/* Preview surface */}
        <div className="flex-1 min-h-0 py-4 flex items-center justify-center bg-black/40 rounded-md relative overflow-auto">
          {attachments.length > 1 && (
            <>
              <Button
                variant="ghost" size="icon"
                onClick={() => step(-1)}
                data-testid="attachment-preview-prev"
                className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 bg-black/40 hover:bg-black/60 text-white z-10"
                aria-label="Previous attachment"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost" size="icon"
                onClick={() => step(1)}
                data-testid="attachment-preview-next"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 bg-black/40 hover:bg-black/60 text-white z-10"
                aria-label="Next attachment"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </>
          )}

          {isImage ? (
            <>
              {!imgLoaded && (
                <Loader2 className="w-6 h-6 animate-spin text-gray-500 absolute" />
              )}
              <img
                key={current.file_id}
                src={href}
                alt={current.original_filename}
                onLoad={() => setImgLoaded(true)}
                style={{ transform: `scale(${zoom})`, transformOrigin: "center center", transition: "transform 100ms ease" }}
                className="max-w-full max-h-full object-contain"
                data-testid="attachment-preview-image"
              />
            </>
          ) : isPdf ? (
            <div
              className="w-full h-full flex justify-center"
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: "top center",
                transition: "transform 100ms ease",
                width: `${100 / zoom}%`,
              }}
            >
              <iframe
                key={current.file_id}
                src={href}
                title={current.original_filename}
                className="w-full h-full min-h-[70vh] bg-white rounded"
                data-testid="attachment-preview-pdf"
              />
            </div>
          ) : (
            <div className="text-center p-8" data-testid="attachment-preview-unsupported">
              <FileText className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <div className="text-white font-medium mb-1">
                {current.original_filename}
              </div>
              <div className="text-xs text-gray-500 mb-4">
                Inline preview isn&apos;t available for this file type.
              </div>
              <a
                href={href}
                download={current.original_filename}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm"
              >
                <Download className="w-4 h-4" />
                Download to open
              </a>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="pt-3 border-t border-white/10 flex items-center justify-between gap-2 flex-wrap">
          {/* Iter195 — Reactions (managers/admins react; reps see counts) */}
          <div className="flex items-center gap-2" data-testid="attachment-preview-reactions">
            <Button
              type="button" size="sm" variant="ghost"
              onClick={() => toggleReaction("thumbs_up")}
              disabled={!canReact}
              data-testid="attachment-react-thumbs-up"
              title={canReact ? "React with thumbs up" : "Only managers can react"}
              className={
                "h-8 px-2 border " +
                (currentReactions.mine.includes("thumbs_up")
                  ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-200"
                  : "bg-white/5 border-white/10 text-gray-300 hover:bg-white/10")
              }
            >
              <ThumbsUp className="w-3.5 h-3.5 mr-1.5" />
              <span className="text-[11px]">{currentReactions.counts.thumbs_up}</span>
            </Button>
            <Button
              type="button" size="sm" variant="ghost"
              onClick={() => toggleReaction("target")}
              disabled={!canReact}
              data-testid="attachment-react-target"
              title={canReact ? "Mark as target — standout proof" : "Only managers can react"}
              className={
                "h-8 px-2 border " +
                (currentReactions.mine.includes("target")
                  ? "bg-amber-500/20 border-amber-500/40 text-amber-200"
                  : "bg-white/5 border-white/10 text-gray-300 hover:bg-white/10")
              }
            >
              <Target className="w-3.5 h-3.5 mr-1.5" />
              <span className="text-[11px]">{currentReactions.counts.target}</span>
            </Button>
          </div>
          <div className="flex items-center gap-2">
            {isImage && isPrivileged && (
              <button
                type="button"
                onClick={retryOcr}
                disabled={ocrRetrying}
                title="Re-run OCR on this screenshot"
                data-testid="attachment-preview-ocr-retry"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 border border-white/10 text-sm text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {ocrRetrying ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <ScanEye className="w-3.5 h-3.5" />
                )}
                {ocrRetrying ? "Retrying…" : "Retry OCR"}
              </button>
            )}
            <a
              href={href}
              download={current.original_filename}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 border border-white/10 text-sm text-white hover:bg-white/10"
              data-testid="attachment-preview-download"
            >
              <Download className="w-3.5 h-3.5" />
              Download
            </a>
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 border border-white/10 text-sm text-white hover:bg-white/10"
              data-testid="attachment-preview-open-new-tab"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open in new tab
            </a>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
